import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';
import React, { useMemo } from 'react';
import { startUpgradeModalFlow } from '~/App/Components/Modals/UpgradeModalFlow';
import { UpgradeModalTrigger } from '~/App/Components/Modals/UpgradeModalFlow/util';

import ProfileImage from '~/App/Components/ProfileImage';
import HeaderActions from '~/App/Modules/TodoList/Components/Greet/HeaderActions';
import RollUpButtonWithTooltip from '~/App/Modules/TodoList/Routes/BulkSelectActions';
import { assetUrl } from '~/helpers';
import { useFeatures } from '~/react-hooks/use-features';
import { Greet, Profile } from '~/types/models';

export type BulkSelectProps = {
  count: number;
  selectedGreets: Greet[];
};
export default function BulkSelect({ count, selectedGreets }: BulkSelectProps) {
  const plandalf = window.plandalf;

  const profiles = useMemo(
    () =>
      selectedGreets.reduce(
        (profiles: Profile[], greet) => [...profiles, ...greet.profiles],
        []
      ),
    [selectedGreets]
  );
  const { canPurchaseRollUpLimitAddOn, greetRollUpLimit } = useFeatures();

  const onUpgradeClick = () => {
    const startRollupPaywall = 'start-rollup-paywall';

    if (
      plandalf.isWorkflowActive(startRollupPaywall) &&
      canPurchaseRollUpLimitAddOn
    ) {
      plandalf.dispatch(startRollupPaywall);
    } else {
      startUpgradeModalFlow({
        trigger: UpgradeModalTrigger.RollupLimit,
        withAddOn: canPurchaseRollUpLimitAddOn,
      });
    }
  };

  //show upgrade if rollup limit is low, grrrowth or addon has 250
  const showUpgrade = (greetRollUpLimit ?? 0) < 10;

  return (
    <div className='flex flex-col gap-y-7 h-full'>
      <div>
        <div className='text-[28px] float-left inline-flex font-bold pb-2'>
          Group actions
        </div>
        <div className='flex gap-x-2.5 flex-wrap float-left mt-2 lg:float-right lg:mt-0'>
          <HeaderActions greet={selectedGreets} showAssign showWorkspace showRollUp />
        </div>
      </div>
      <div className='flex flex-col gap-y-5 h-full'>
        <div className='text-xl font-bold'>
          {pluralize('contact', count, true)} selected
        </div>
        <div className='flex justify-between space-x-1 h-full'>
          <div className='flex gap-x-7 flex-wrap max-w-[480px] h-fit 2xl:max-w-2xl'>
            {profiles.map((profile) => (
              <div className='flex gap-x-2.5 my-2 h-fit' key={profile.id}>
                <ProfileImage profile={profile} size={50} key={profile.id} />
                <div className='self-center'>
                  <div className='v2-text-neutral-900 text-base'>
                    {profile.name}
                  </div>
                  {profile.work.current && (
                    <div className='text-xs v2-text-neutral-500'>
                      {profile.work.current}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <div className='text-base flex flex-col justify-between bg-neutral-50 space-y-4 w-[230px] xl:w-[330px] h-full p-7 rounded-md'>
            <div className='flex flex-col space-y-4'>
              <div className='flex font-bold items-center space-x-2'>
                <FontAwesomeIcon icon={faInfoCircle} className='h-5 w-5' />
                <span>What is Roll-up?</span>
              </div>
              <div>
                Roll-up combines tasks on your list so you can send 1 message to
                up to 250 contacts at a time.
              </div>
              {showUpgrade && (
                <div>
                  You have to be on Grrrowth Plan or above to be able to create
                  Roll-ups with 3 or more tasks.{' '}
                  <span
                    className='underline cursor-pointer'
                    onClick={onUpgradeClick}
                  >
                    Upgrade today
                  </span>
                  .
                </div>
              )}
            </div>
            <img src={assetUrl('assets/images/joro/joro-roll.png')} />
          </div>
        </div>
      </div>
    </div>
  );
}
